import React, { useState, useRef, useEffect } from 'react'
import { styles } from './index.sass'
import { Button, Input, message, Spin, Card, Tooltip } from 'antd'
import { ArrowUpOutlined, CopyOutlined } from '@ant-design/icons'
import axios from '../../api/axios_ml'

const { TextArea } = Input

export const Social = () => {
  const [loading, setLoading] = useState(false)
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const messagesEndRef = useRef(null)

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, loading])

  const handleSendMessage = async () => {
    if (!inputValue.trim()) {
      message.error('Please enter a message')
      return
    }

    const userMessage = {
      id: Date.now(),
      text: inputValue.trim(),
      sender: 'user',
      timestamp: new Date().toISOString()
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setLoading(true)

    try {
      const response = await axios.postJson(
        '/api/ml/social-chat',
        {
          messages: [...messages, userMessage]
        }
      )

      if (response && response.response) {
        const botMessage = {
          id: Date.now() + 1,
          text: response.response,
          sender: 'bot',
          timestamp: new Date().toISOString()
        }
        setMessages(prev => [...prev, botMessage])
      } else {
        message.error('No response from the chatbot')
      }
    } catch (err) {
      console.error('Error chatting with social bot:', err)
      message.error(
        (err.detail) || (err.message || 'Failed to chat with social bot. Please try again.')
      )
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleCopyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      message.success('Message copied to clipboard')
    } catch (err) {
      console.error('Error copying message:', err)
      message.error('Failed to copy message')
    }
  }

  const formatText = (text) => {
    // Regular expressions to match URLs and hashtags
    const urlRegex = /(https?:\/\/[^\s]+)/g
    const hashtagRegex = /#(\w+)/g
    
    const isImageUrl = (url) => {
      // Check for image file extensions or image format parameters
      const result = /\.(jpeg|jpg|gif|png|webp)/i.test(url) || 
                    /(?:&|\?)fm=(jpeg|jpg|gif|png|webp)/i.test(url) ||
                    /unsplash\.com/i.test(url)
      return result
    }

    return text.split('\n').map((line, index) => {
      const parts = []
      let lastIndex = 0
      
      // Match URLs
      let urlMatch
      while ((urlMatch = urlRegex.exec(line)) !== null) {
        const [fullUrl] = urlMatch
        const start = urlMatch.index
        
        // Add text before the URL
        if (start > lastIndex) {
          const beforeText = line.slice(lastIndex, start)
          // Check for hashtags in the beforeText
          if (hashtagRegex.test(beforeText)) {
            const hashtagParts = []
            let hashtagLastIndex = 0
            let hashtagMatch
            while ((hashtagMatch = hashtagRegex.exec(beforeText)) !== null) {
              const [fullHashtag, tagName] = hashtagMatch
              const hashtagStart = hashtagMatch.index
              
              if (hashtagStart > hashtagLastIndex) {
                hashtagParts.push(beforeText.slice(hashtagLastIndex, hashtagStart))
              }
              
              hashtagParts.push(
                <a 
                  key={`hashtag-${index}-${hashtagStart}`}
                  href={`/search?tag=${tagName}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hashtag-link"
                >
                  {fullHashtag}
                </a>
              )
              
              hashtagLastIndex = hashtagMatch.index + fullHashtag.length
            }
            
            if (hashtagLastIndex < beforeText.length) {
              hashtagParts.push(beforeText.slice(hashtagLastIndex))
            }
            
            parts.push(hashtagParts)
          } else {
            parts.push(beforeText)
          }
        }
        
        // Check if it's an image URL and render as image
        if (isImageUrl(fullUrl)) {
          parts.push(
            <div key={`image-${index}-${start}`} className="image-container">
              <img 
                src={fullUrl} 
                alt="Generated content" 
                className="chat-image"
                onError={(e) => {
                  console.error('Failed to load image:', fullUrl)
                  e.target.style.display = 'none'
                }}
              />
            </div>
          )
        } else {
          parts.push(
            <a 
              key={`url-${index}-${start}`}
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="url-link"
            >
              {fullUrl}
            </a>
          )
        }
        
        lastIndex = urlRegex.lastIndex
      }
      
      // Add remaining text after the last URL
      if (lastIndex < line.length) {
        const remainingText = line.slice(lastIndex)
        if (hashtagRegex.test(remainingText)) {
          const hashtagParts = []
          let hashtagLastIndex = 0
          let hashtagMatch
          while ((hashtagMatch = hashtagRegex.exec(remainingText)) !== null) {
            const [fullHashtag, tagName] = hashtagMatch
            const hashtagStart = hashtagMatch.index
            
            if (hashtagStart > hashtagLastIndex) {
              hashtagParts.push(remainingText.slice(hashtagLastIndex, hashtagStart))
            }
            
            hashtagParts.push(
              <a 
                key={`hashtag-${index}-${lastIndex + hashtagStart}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hashtag-link"
              >
                {fullHashtag}
              </a>
            )
            
            hashtagLastIndex = hashtagMatch.index + fullHashtag.length
          }
          
          if (hashtagLastIndex < remainingText.length) {
            hashtagParts.push(remainingText.slice(hashtagLastIndex))
          }
          
          parts.push(hashtagParts)
        } else {
          parts.push(remainingText)
        }
      }
      
      return (
        <p key={index} className="message-line">
          {parts}
        </p>
      )
    })
  }

  return (
    <div className={styles}>
      <div className="container">
        <h2>Social Media Chatbot</h2>
        
        <Card className="social-card">
          <div className="chat-container">
            <div className="chat-messages">
              {messages.map(message => (
                <div key={message.id} className={`message ${message.sender}`}>
                  <div className="message-content">
                    {message.sender === 'bot' && (
                      <Tooltip title="Copy message">
                        <Button
                          type="text"
                          icon={<CopyOutlined />}
                          onClick={() => handleCopyMessage(message.text)}
                          className="copy-button"
                        />
                      </Tooltip>
                    )}
                    {formatText(message.text)}
                  </div>
                  <div className="message-timestamp">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="message bot typing">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            
            <div className="chat-input">
              <TextArea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about social media content... e.g., How to write an engaging LinkedIn post about AI in healthcare?"
                rows={3}
                disabled={loading}
              />
              <Button
                type="primary"
                onClick={handleSendMessage}
                loading={loading}
                icon={<ArrowUpOutlined />}
                disabled={!inputValue.trim() || loading}
                className="send-button"
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
